import classNames from "classnames";
import DOMPurify from "dompurify";
import React from "react";

export enum ValidationMessageType {
  ERROR = "error",
  WARNING = "warning"
}

interface ValidationMessageProps {
  message?: string;
  type?: ValidationMessageType;
  className?: string;
  id?: string;
}

/**
 * ValidationMessage component for displaying error and warning messages.
 *
 * Includes proper ARIA attributes for accessibility:
 * - role="alert" ensures screen readers announce errors immediately
 * - aria-live="polite" for warning messages
 * - Accepts id prop to enable aria-describedby linking from inputs
 *
 * @param message - The validation message to display
 * @param type - Type of message (error or warning)
 * @param className - Additional CSS classes
 * @param id - ID for aria-describedby linking
 */
const ValidationMessage = ({
  message,
  type = ValidationMessageType.ERROR,
  className = "",
  id
}: ValidationMessageProps) => {
  if (!message) {
    return null;
  }

  const baseClass = classNames("mt-xxs text-t-sm font-t-semi-bold", className, {
    "text-error-200": type === ValidationMessageType.ERROR,
    "text-warning-200": type === ValidationMessageType.WARNING
  });

  // Use role="alert" for errors (announced immediately) and aria-live="polite" for warnings
  const ariaRole = type === ValidationMessageType.ERROR ? "alert" : undefined;
  const ariaLive = type === ValidationMessageType.WARNING ? "polite" : undefined;

  return (
    <span
      id={id}
      className={baseClass}
      role={ariaRole}
      aria-live={ariaLive}
      aria-atomic="true"
    >
      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />
    </span>
  );
};

export default ValidationMessage;
